import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import SimpleMarkdown from '../components/SimpleMarkdown';
import { formatDateTime, formatMoney } from './billingShared';

function fmtLessonWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function billingLabel(billing) {
  if (!billing) return null;
  if (billing.status === 'debt') {
    return { text: `Долг ${formatMoney(billing.debt)}`, tone: 'text-error' };
  }
  if (billing.status === 'prepaid') {
    return { text: `Предоплата ${formatMoney(billing.prepaid)}`, tone: 'text-success' };
  }
  return { text: 'Баланс 0 ₽', tone: '' };
}

export default function ParentHomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/parent/home/');
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось загрузить кабинет родителя.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить кабинет родителя.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmReport(id) {
    setConfirmingId(id);
    setError('');
    try {
      await api.post(`/payments/lesson-reports/${id}/confirm/`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось подтвердить занятие.');
    } finally {
      setConfirmingId(null);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка...</p>
      </div>
    );
  }

  const children = data?.children || [];
  const pending = data?.pending_reports || [];
  const balance = billingLabel(data?.billing);
  const nextLesson = data?.next_lesson;
  const unread = Number(data?.unread_messages) || 0;

  return (
    <div className="parent-home-page">
      <div className="page-header">
        <h1>Кабинет родителя</h1>
        <p className="page-subtitle">
          Занятия, оплата и прогресс детей. Связь с преподавателем — в сообщениях.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="billing-section parent-home-summary">
        <h2>Семья</h2>
        <ul className="parent-summary-facts">
          <li>
            <span className="text-secondary">Счёт</span>
            {' '}
            {balance ? (
              <strong className={balance.tone || undefined}>{balance.text}</strong>
            ) : (
              '—'
            )}
            {' · '}
            <Link to="/billing">Баланс и пополнение</Link>
          </li>
          <li>
            <span className="text-secondary">Ближайшее занятие</span>
            {' '}
            {nextLesson ? (
              <>
                <strong>{fmtLessonWhen(nextLesson.start)}</strong>
                {nextLesson.student_name ? ` · ${nextLesson.student_name}` : ''}
                {nextLesson.teacher_name ? ` · ${nextLesson.teacher_name}` : ''}
                {' · '}
                <Link to={`/my-schedule${nextLesson.student_id ? `?student_id=${nextLesson.student_id}` : ''}`}>
                  Расписание
                </Link>
              </>
            ) : (
              <>
                пока нет
                {' · '}
                <Link to="/my-schedule">Расписание</Link>
              </>
            )}
          </li>
          <li>
            <span className="text-secondary">Сообщения</span>
            {' '}
            {unread > 0 ? (
              <>
                <strong>{unread} непрочитанных</strong>
                {' · '}
              </>
            ) : (
              'нет новых · '
            )}
            <Link
              to={
                nextLesson?.student_id
                  ? `/my-messages?student_id=${nextLesson.student_id}`
                  : children[0]?.id
                    ? `/my-messages?student_id=${children[0].id}`
                    : '/my-messages'
              }
            >
              Открыть
            </Link>
          </li>
        </ul>
      </section>

      {pending.length > 0 && (
        <section className="billing-section">
          <h2>Подтвердите занятия</h2>
          <p className="text-secondary">После подтверждения сумма спишется с семейного счёта.</p>
          <ul className="list-simple">
            {pending.map((r) => (
              <li key={r.id} className="billing-report-card">
                <div>
                  <strong>{r.topic || 'Занятие'}</strong>
                  {r.student_name ? ` · ${r.student_name}` : ''}
                  {' · '}
                  {r.duration_minutes} мин · {formatMoney(r.amount)}
                  <div className="text-secondary">{r.description}</div>
                  {r.homework ? (
                    <div className="billing-homework">
                      <span>ДЗ:</span>
                      <SimpleMarkdown text={r.homework} />
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={confirmingId === r.id}
                  onClick={() => confirmReport(r.id)}
                >
                  {confirmingId === r.id ? 'Сохранение…' : 'Подтвердить'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="billing-section">
        <h2>Дети</h2>
        {children.length === 0 ? (
          <p className="text-secondary">
            Пока нет привязанных детей. Нужна секретная ссылка от ученика или преподавателя —
            откройте её и войдите или зарегистрируйтесь.
          </p>
        ) : (
          <ul className="parent-child-list">
            {children.map((child) => (
              <li key={child.id} className="parent-child-card">
                <div className="parent-child-card-title">
                  {child.display_name}
                  <span className="text-secondary"> @{child.username}</span>
                  {!child.has_parent_questionnaire ? (
                    <span className="parent-child-badge">анкета не заполнена</span>
                  ) : null}
                </div>
                <ul className="parent-child-facts text-secondary">
                  <li>
                    Ближайшее:{' '}
                    {child.next_lesson
                      ? fmtLessonWhen(child.next_lesson.start)
                      : 'нет в расписании'}
                  </li>
                  <li>
                    Последнее занятие:{' '}
                    {child.last_lesson
                      ? `${fmtLessonWhen(child.last_lesson.at)}${
                          child.last_lesson.topic ? ` · ${child.last_lesson.topic}` : ''
                        }`
                      : 'пока нет'}
                  </li>
                  <li>
                    На сайте за 7 дней:{' '}
                    {child.has_recent_activity
                      ? `да${child.recent_activity_at ? ` · ${formatDateTime(child.recent_activity_at)}` : ''}`
                      : 'не замечено'}
                  </li>
                </ul>
                <div className="parent-child-card-links">
                  <Link
                    to={`/parent/children/${child.id}`}
                    className="btn btn-primary btn-sm"
                  >
                    Подробнее
                  </Link>
                  <Link
                    to={`/my-messages?student_id=${child.id}`}
                    className="btn btn-outline btn-sm"
                  >
                    Сообщения
                  </Link>
                  {!child.has_parent_questionnaire ? (
                    <Link
                      to={`/parent/children/${child.id}?tab=questionnaire`}
                      className="btn btn-outline btn-sm"
                    >
                      Заполнить анкету
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
